import { CleanupFunction, DecisionContext, isLanguageChanged } from "@/dbot/common/commonDomain";
import { identity } from "lodash";
import { DecisionType } from "@/common/decisions";
import {
  BooleanFieldModel,
  LocalizedStringFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { LocalizedText } from "@/common/common";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import { Language } from "@/i18n/languages";
import Big from "big.js";

export enum ShareCapitalDuration {
  untilGM = "untilGM",
  months = "months",
}

export type AcquireOwnSharesPowerOfAttorney = {
  type: DecisionType.acquireOwnSharesPowerOfAttorney;
  calculateShareCapitalFromShares?: boolean;
  shareCapitalValue?: number;
  minimumSharePrice?: number;
  maximumSharePrice?: number;
  isProportionallyAdjusted?: boolean;
  otherProperties: LocalizedText[];
  shareCapitalDuration: ShareCapitalDuration;
  shareCapitalDurationMonths?: number;
};

export class AcquireOwnSharesPowerOfAttorneyForm {
  readonly type = DecisionType.acquireOwnSharesPowerOfAttorney;
  context: DecisionContext<AcquireOwnSharesPowerOfAttorney>;
  data?: AcquireOwnSharesPowerOfAttorney;
  calculateShareCapitalFromShares: RadioFieldModel<boolean | undefined>;
  totalSharesValue: NumberFieldModel;
  shareCapitalValue: NumberFieldModel;
  minimumSharePrice: NumberFieldModel;
  maximumSharePrice: NumberFieldModel;
  isProportionallyAdjusted: BooleanFieldModel;
  otherProperties: LocalizedStringFieldModel[];
  shareCapitalDuration: RadioFieldModel<ShareCapitalDuration>;
  shareCapitalDurationMonths: NumberFieldModel;

  constructor(
    context: DecisionContext<AcquireOwnSharesPowerOfAttorney>,
    data?: AcquireOwnSharesPowerOfAttorney,
  ) {
    this.context = context;
    this.data = data;
    this.calculateShareCapitalFromShares = new RadioFieldModel<boolean | undefined>({
      value: data?.calculateShareCapitalFromShares,
      required: true,
      options: [false, true],
      idFn: identity,
      labelFn: (v) =>
        v
          ? {
              key: "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalValue.from-shares",
            }
          : { key: "decisions.acquireOwnSharesPowerOfAttorney.shareCapitalValue.from-total" },
    });
    this.shareCapitalValue = new NumberFieldModel({
      value: data?.shareCapitalValue,
      isPositive: true,
      required: true,
      onChange: (value) => {
        if (value !== undefined) {
          this.totalSharesValue.setInputValue(calculateNumberOfShares(value, this.nominalValue));
        }
      },
      validator: (value) => {
        if (value && value > this.maximumShareCapitalValue) {
          return {
            key: "number-field.smaller-or-equal-than-n",
            params: { n: { type: "number", value: this.maximumShareCapitalValue } },
          };
        }
      },
    });
    this.totalSharesValue = new NumberFieldModel({
      value: data?.shareCapitalValue
        ? calculateNumberOfShares(data.shareCapitalValue, this.nominalValue)
        : undefined,
      isPositive: true,
      required: true,
      onChange: (value) => {
        if (value !== undefined) {
          this.shareCapitalValue.setAndCommitInputValue(
            calculateShareCapital(value, this.nominalValue),
          );
        }
      },
      validator: (value) => {
        if (value && value > this.maximumNumberOfShares) {
          return {
            key: "number-field.smaller-or-equal-than-n",
            params: { n: { type: "number", value: this.maximumNumberOfShares } },
          };
        }
      },
    });
    this.minimumSharePrice = new NumberFieldModel({
      value: data?.minimumSharePrice,
      isPositive: true,
      required: true,
    });
    this.maximumSharePrice = new NumberFieldModel({
      value: data?.maximumSharePrice,
      isPositive: true,
      required: true,
    });
    this.isProportionallyAdjusted = new BooleanFieldModel({
      value: data?.isProportionallyAdjusted ?? false,
      required: true,
    });
    this.otherProperties =
      data?.otherProperties.map(
        (t) =>
          new LocalizedStringFieldModel(
            new StringFieldModel({
              value: t.text,
              required: true,
            }),
            t.language,
          ),
      ) ?? [];
    this.shareCapitalDuration = new RadioFieldModel<ShareCapitalDuration>({
      value: data?.shareCapitalDuration ?? ShareCapitalDuration.untilGM,
      options: Object.values(ShareCapitalDuration),
      idFn: identity,
      labelFn: (v) => ({
        key: `decisions.acquireOwnSharesPowerOfAttorney.shareCapitalDuration.${v}`,
      }),
    });
    this.shareCapitalDurationMonths = new NumberFieldModel({
      value: data?.shareCapitalDurationMonths,
      isPositive: true,
      isInteger: true,
    });
    makeAutoObservable(this, { type: false }, { autoBind: true });
  }

  init(): CleanupFunction {
    const cleanupFunctions = [
      autorun(() => {
        this.reactToLanguageChanges();
      }),
    ];
    return () => {
      cleanupFunctions.forEach((f) => f());
    };
  }

  reactToLanguageChanges() {
    runInAction(() => {
      if (isLanguageChanged(this.context.documentLanguages, this.otherProperties)) {
        this.otherProperties = updateTextModels(
          this.context.documentLanguages,
          this.otherProperties,
        );
      }
    });
  }

  get documentLanguages(): Language[] {
    return this.context.documentLanguages;
  }

  get nominalValue() {
    return this.context.inputState?.sharenominalValue ?? 1;
  }

  get shareCapital() {
    return Big(this.totalNumberOfShares).mul(this.nominalValue).toNumber();
  }

  get maximumNumberOfShares() {
    return calculateNumberOfShares(this.maximumShareCapitalValue, this.nominalValue);
  }

  get maximumShareCapitalValue() {
    return Big(this.shareCapital).minus(30000).toNumber();
  }

  get totalNumberOfShares() {
    return (
      this.context.inputState?.numberOfShares.reduce((acc, val) => acc + val.numberOfShares, 0) ?? 0
    );
  }

  setDurationMonthsLimit() {
    if (
      this.shareCapitalDurationMonths.parsedValue &&
      this.shareCapitalDurationMonths.parsedValue > 24
    ) {
      this.shareCapitalDurationMonths.setAndCommitInputValue(24);
    }
  }

  get isCalculateShareCapitalSelected() {
    return this.calculateShareCapitalFromShares.value !== undefined;
  }

  get showShareCapitalValue() {
    return this.calculateShareCapitalFromShares.value === false;
  }

  validate(): boolean {
    this.calculateShareCapitalFromShares.validate();
    this.shareCapitalValue.validate();
    this.minimumSharePrice.validate();
    this.maximumSharePrice.validate();
    this.isProportionallyAdjusted.validate();
    this.otherProperties.forEach((t) => t.model.validate());
    this.shareCapitalDurationMonths.validate();

    return this.isValid;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return (
      this.calculateShareCapitalFromShares.isValid &&
      this.shareCapitalValue.isValid &&
      this.minimumSharePrice.isValid &&
      this.maximumSharePrice.isValid &&
      this.isProportionallyAdjusted.isValid &&
      this.otherProperties.every((t) => t.model.isValid) &&
      this.shareCapitalDurationMonths.isValid
    );
  }

  get isDurationMonths(): boolean {
    return this.shareCapitalDuration.value === ShareCapitalDuration.months;
  }
}

function updateTextModels(
  languages: Language[],
  models?: LocalizedStringFieldModel[],
): LocalizedStringFieldModel[] {
  return languages.map((lang) => {
    const localized = models?.find((t) => t.locale === lang);
    if (localized) {
      return localized;
    }
    return new LocalizedStringFieldModel(new StringFieldModel({ required: true }), lang);
  });
}

function calculateNumberOfShares(shareCapital: number, nominalValue: number) {
  return Big(shareCapital).div(nominalValue).toNumber();
}

function calculateShareCapital(numberOfShares: number, nominalValue: number) {
  return Big(numberOfShares).mul(nominalValue).toNumber();
}
